import React, { FC, useEffect } from "react";
import { Routes } from "@Libs/Router/Components/Routes/Routes";
import { Route } from "@Libs/Router/Components/Route/Route";
import { ChooseFormulaPage } from "../../../Components/ServiceOfferAcceptanceFlow/ChooseFormulaPage/ChooseFormulaPage";
import { useServiceOffer } from "../../../hooks/useServiceOffer";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import { useParams } from "@Libs/Router/hooks/useParams";
import { Match, On, Other } from "@Libs/Utils/Components/Match/Match";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { acceptServiceOffer } from "../../../services/serviceOffers";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { useMetadata } from "@Libs/Events/hooks/useMetadata";
import { ServiceOfferAcceptanceServiceFeeFlow } from "@modules/ServiceFees/Containers/ServiceOfferAcceptanceServiceFeeFlow/ServiceOfferAcceptanceServiceFeeFlow";
import { ServiceOfferAcceptanceSubscriptionFlow } from "@modules/Subscriptions/Containers/ServiceOfferAcceptanceSubscriptionFlow/ServiceOfferAcceptanceSubscriptionFlow";
import { CancelledServiceOffer } from "@modules/ServiceAssignment/Components/ServiceOfferAcceptanceFlow/CancelledServiceOffer/CancelledServiceOffer";

export const ServiceOfferAcceptanceFlowRouter: FC = () => {
  const { serviceOfferId } = useParams([
    "SERVICE_OFFERS",
    "SERVICE_OFFER",
    "ACCEPTANCE",
  ]);
  const navigate = useNavigate();
  const paymentRedirectStatus = useSearchParams("redirect_status");
  const {
    setMetadata,
    metadata: { context },
  } = useMetadata();

  useEffect(() => {
    setMetadata({ context: "serviceOfferAcceptanceFlow" });
  }, []);

  const isContextSet = context === "serviceOfferAcceptanceFlow";

  const {
    serviceOffer,
    apiError: serviceOfferLoadingError,
    isLoading: isServiceOfferLoading,
    isError: isServiceOfferLoadingError,
  } = useServiceOffer(serviceOfferId);

  const { isLoading: isCurrentUserLoading, currentUser } = useCurrentUser({
    includes: ["currentSubscription"],
  });

  const redirectToConfirmation = () => {
    navigate("LEGACY_SERVICE_OFFER_FLOWS_CONFIRMATION_PAGE", {
      reload: true,
      queryParams: {
        conversation_id: String(serviceOffer.conversationId),
      },
    });
  };

  const {
    mutate: accept,
    isLoading: isServiceOfferBeingAccepted,
    isSuccess: wasServiceOfferAccepted,
  } = useDataMutation(acceptServiceOffer, {
    onSuccess: () => {
      redirectToConfirmation();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    if (
      serviceOffer &&
      !(serviceOffer?.state === "accepted") &&
      currentUser?.currentSubscription &&
      !isServiceOfferBeingAccepted &&
      !paymentRedirectStatus
    ) {
      accept(serviceOffer.id);
    }
  }, [serviceOffer, currentUser?.currentSubscription]);

  useEffect(() => {
    if (wasServiceOfferAccepted || serviceOffer?.state === "accepted") {
      redirectToConfirmation();
    }
  }, [wasServiceOfferAccepted, serviceOffer]);

  if (isServiceOfferLoading || isCurrentUserLoading || !isContextSet) {
    return <LoadingScreen />;
  }

  if (
    serviceOffer?.state === "cancelled" ||
    serviceOffer?.state === "deleted"
  ) {
    return <CancelledServiceOffer />;
  }

  if (
    !isServiceOfferLoadingError &&
    (currentUser.currentSubscription || serviceOffer?.state === "accepted") &&
    !paymentRedirectStatus
  ) {
    return null;
  }

  return (
    <Match value={serviceOfferLoadingError?.response?.status}>
      <On valueBetween={[400, 499]}>
        <Navigate to="NOT_FOUND" reload />
      </On>

      <On valueBetween={[500, 599]}>
        <Navigate to="SERVER_ERROR" reload />
      </On>

      <Other>
        <Routes>
          <Route fragments="CHOOSE_FORMULA" element={<ChooseFormulaPage />} />
          <Route
            fragments="SUBSCRIBE"
            matchAny
            element={<ServiceOfferAcceptanceSubscriptionFlow />}
          />
          <Route
            fragments="SERVICE_FEE"
            matchAny
            element={<ServiceOfferAcceptanceServiceFeeFlow />}
          />
          <Route
            fragments="ROOT"
            matchAny
            element={
              <Navigate
                replace
                to={[
                  "SERVICE_OFFERS",
                  "SERVICE_OFFER",
                  "ACCEPTANCE",
                  "CHOOSE_FORMULA",
                ]}
                params={{ serviceOfferId }}
              />
            }
          />
        </Routes>
      </Other>
    </Match>
  );
};
